import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { toast } from 'sonner';
import { MagnifyingGlass, PencilSimple, Trash, UploadSimple, X, FloppyDisk, Image, Plus, Copy } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cldThumb } from '@/utils/cloudinary';

const Catalog = () => {
  const { selectedBusiness } = useOutletContext();
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ code: '', name: '', category: '', description: '', price: '', material: '', dimensions: '', finish: '', stock: '', notes: '' });
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (selectedBusiness) fetchProducts();
  }, [selectedBusiness]);

  const fetchProducts = async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const res = await api.get(`/catalog/products/${selectedBusiness.id}`);
      setProducts(res.data);
    } catch {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/catalog/upload/${selectedBusiness.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      if (res.data.skipped?.length > 0) {
        toast.warning(`Omitidos por codigo duplicado: ${res.data.skipped.join(', ')}`, { duration: 8000 });
      }
      if (res.data.rejected_no_code?.length > 0) {
        toast.error(`Rechazados sin codigo: ${res.data.rejected_no_code.join(', ')}`, { duration: 8000 });
      }
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir archivo');
    }
    e.target.value = '';
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setEditForm({
      code: product.code || '',
      name: product.name || '',
      category: product.category || '',
      description: product.description || '',
      price: product.price || 0,
      material: product.material || '',
      dimensions: product.dimensions || '',
      finish: product.finish || '',
      stock: product.stock || '',
      notes: product.notes || '',
    });
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editProduct) return;
    try {
      await api.put(`/catalog/products/${selectedBusiness.id}/${editProduct.id}`, editForm);
      toast.success('Producto actualizado');
      setIsEditOpen(false);
      setEditProduct(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Eliminar "${product.name}"?`)) return;
    try {
      await api.delete(`/catalog/products/${selectedBusiness.id}/${product.id}`);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleAddProduct = async () => {
    if (!addForm.code.trim()) {
      toast.error('El codigo es requerido');
      return;
    }
    if (!addForm.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      await api.post(`/catalog/products/${selectedBusiness.id}`, addForm);
      toast.success('Producto agregado');
      setIsAddOpen(false);
      setAddForm({ code: '', name: '', category: '', description: '', price: '', material: '', dimensions: '', finish: '', stock: '', notes: '' });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar');
    }
  };

  const handleDuplicate = (product) => {
    setAddForm({
      code: '',
      name: product.name + ' (copia)',
      category: product.category || '',
      description: product.description || '',
      price: product.price || '',
      material: product.material || '',
      dimensions: product.dimensions || '',
      finish: product.finish || '',
      stock: product.stock || '',
      notes: product.notes || '',
    });
    setIsAddOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editProduct) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(
        `/catalog/products/${selectedBusiness.id}/image?product_id=${editProduct.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Imagen subida');
      setEditProduct({ ...editProduct, image_url: res.data.url });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir imagen');
    }
    e.target.value = '';
  };

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.code?.toLowerCase().includes(q) ||
      p.name?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.material?.toLowerCase().includes(q)
    );
  });

  const categories = [...new Set(filtered.map((p) => p.category || 'General'))];

  if (!selectedBusiness) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="text-sm text-[var(--text-muted)]">Selecciona un negocio</p>
      </div>
    );
  }

  return (
    <div className="h-full p-6 overflow-y-auto" data-testid="catalog-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl tracking-tight font-black text-[var(--text)] mb-1">Catalogo</h1>
          <p className="text-sm text-[var(--text-muted)]">{selectedBusiness.name} - {products.length} productos</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.csv"
            onChange={handleUploadExcel}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => setIsAddOpen(true)}
            data-testid="add-product-btn"
          >
            <Plus size={18} weight="bold" className="mr-2" />
            Agregar Producto
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-[#1D4ED8] hover:bg-[#1E40AF]"
            data-testid="upload-catalog-btn"
          >
            <UploadSimple size={18} weight="bold" className="mr-2" />
            Subir Excel
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar producto, categoria, material..."
          className="pl-10"
          data-testid="catalog-search-input"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1D4ED8] border-t-transparent" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-sm text-[var(--text-muted)] mb-2">No hay productos en el catalogo</p>
          <p className="text-xs text-slate-600">Sube un archivo Excel para comenzar</p>
        </div>
      ) : (
        categories.map((cat) => (
          <div key={cat} className="mb-6">
            <h2 className="text-base font-bold text-[var(--text)] mb-3 px-1">{cat}</h2>
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] overflow-hidden">
              <table className="w-full" data-testid={`catalog-table-${cat}`}>
                <thead className="bg-[var(--bg)] border-b border-[var(--border)]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Codigo</th>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Producto</th>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)] hidden sm:table-cell">Descripcion</th>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Precio</th>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)] hidden md:table-cell">Material</th>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)] hidden md:table-cell">Stock</th>
                    <th className="px-3 py-2 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered
                    .filter((p) => (p.category || 'General') === cat)
                    .map((product) => (
                      <tr key={product.id} className="border-b border-[var(--border)] hover:bg-[var(--bg)] transition-colors" data-testid={`product-row-${product.id}`}>
                        <td className="px-3 py-2 text-xs text-[var(--text-muted)] font-mono">{product.code || '-'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {product.image_url && (
                              <img src={cldThumb(product.image_url, 80)} alt="" loading="lazy" className="w-8 h-8 rounded object-cover" />
                            )}
                            <span className="text-sm font-bold text-[var(--text)]">{product.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-[var(--text-muted)] max-w-[200px] truncate hidden sm:table-cell">{product.description}</td>
                        <td className="px-3 py-2 text-sm font-bold text-[var(--text)]">Q{product.price?.toFixed(2)}</td>
                        <td className="px-3 py-2 text-xs text-[var(--text-muted)] hidden md:table-cell">{product.material}</td>
                        <td className="px-3 py-2 hidden md:table-cell">
                          <span className={`text-xs px-1.5 py-0.5 rounded-[12px] font-bold ${
                            product.stock?.toLowerCase() === 'si' || product.stock?.toLowerCase() === 'yes'
                              ? 'bg-emerald-50 text-emerald-600'
                              : product.stock?.toLowerCase() === 'no'
                              ? 'bg-red-50 text-red-600'
                              : 'bg-[var(--bg)] text-[var(--text-muted)]'
                          }`}>{product.stock || '-'}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(product)}
                              data-testid={`edit-product-${product.id}`}
                              title="Editar"
                            >
                              <PencilSimple size={14} weight="bold" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDuplicate(product)}
                              data-testid={`duplicate-product-${product.id}`}
                              title="Duplicar"
                            >
                              <Copy size={14} weight="bold" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(product)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`delete-product-${product.id}`}
                              >
                                <Trash size={14} weight="bold" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Edit Product Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => { setIsEditOpen(open); if (!open) setEditProduct(null); }}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="edit-product-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Codigo</Label>
                <Input value={editForm.code || ''} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} placeholder="Ej: CH-001" />
              </div>
              <div>
                <Label>Nombre</Label>
                <Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-product-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Input value={editForm.category || ''} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
              </div>
              <div>
                <Label>Precio</Label>
                <Input type="number" value={editForm.price || ''} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Descripcion</Label>
              <Input value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Material</Label>
                <Input value={editForm.material || ''} onChange={(e) => setEditForm({ ...editForm, material: e.target.value })} />
              </div>
              <div>
                <Label>Medidas</Label>
                <Input value={editForm.dimensions || ''} onChange={(e) => setEditForm({ ...editForm, dimensions: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Acabado</Label>
                <Input value={editForm.finish || ''} onChange={(e) => setEditForm({ ...editForm, finish: e.target.value })} />
              </div>
              <div>
                <Label>Stock</Label>
                <Input value={editForm.stock || ''} onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>

            {/* Image */}
            <div>
              <Label>Imagen</Label>
              {editProduct?.image_url && (
                <img src={cldThumb(editProduct.image_url, 200)} alt="" loading="lazy" className="w-20 h-20 rounded object-cover mt-1 mb-2" />
              )}
              <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <Button variant="outline" size="sm" onClick={() => imageInputRef.current?.click()} className="mt-1" data-testid="upload-product-image-btn">
                <Image size={14} weight="bold" className="mr-1" /> Subir Imagen
              </Button>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setIsEditOpen(false); setEditProduct(null); }}>Cancelar</Button>
              <Button onClick={handleSaveEdit} data-testid="save-product-btn">
                <FloppyDisk size={16} weight="bold" className="mr-1" /> Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agregar Producto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="add-product-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Codigo *</Label>
                <Input value={addForm.code} onChange={(e) => setAddForm({ ...addForm, code: e.target.value })} placeholder="Ej: CH-001" data-testid="add-product-code" />
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Nombre del producto" data-testid="add-product-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Input value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} placeholder="Ej: Chapas, Tuberia" />
              </div>
              <div>
                <Label>Precio</Label>
                <Input type="number" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Descripcion</Label>
              <Input value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="Descripcion breve" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Material</Label>
                <Input value={addForm.material} onChange={(e) => setAddForm({ ...addForm, material: e.target.value })} placeholder="Ej: Metal, Madera" />
              </div>
              <div>
                <Label>Medidas</Label>
                <Input value={addForm.dimensions} onChange={(e) => setAddForm({ ...addForm, dimensions: e.target.value })} placeholder="Ej: 1 1/2 x 3mts" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Acabado</Label>
                <Input value={addForm.finish} onChange={(e) => setAddForm({ ...addForm, finish: e.target.value })} placeholder="Ej: Cromado, Negro" />
              </div>
              <div>
                <Label>Stock</Label>
                <Input value={addForm.stock} onChange={(e) => setAddForm({ ...addForm, stock: e.target.value })} placeholder="Si / No / cantidad" />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Info adicional, promociones" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancelar</Button>
              <Button onClick={handleAddProduct} data-testid="save-new-product-btn">
                <Plus size={16} weight="bold" className="mr-1" /> Agregar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Catalog;
